import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";

import Search from "../../../assets/images/comman/search-outline.svg";
import MenuHorizental from "../../../assets/images/comman/dots-horizontal-outline.svg";
import EditPatient from "../../../assets/images/OPD-patients/EditPatient.svg";
import Assessment from "../../../assets/images/OPD-patients/Assessment.svg";
import OPDIDPrint from "../../../assets/images/OPD-patients/print.svg";
import ManualPrescription from "../../../assets/images/OPD-patients/ManualPrescription .svg";
import PaymentReceipt from "../../../assets/images/OPD-patients/PaymentReceipt .svg";
import XRayReport from "../../../assets/images/OPD-patients/X-RayReport.svg";
import ScanningReport from "../../../assets/images/OPD-patients/ScanningReport.svg";
import LabReport from "../../../assets/images/OPD-patients/LabReport.svg";
import CancelConsultation from "../../../assets/images/OPD-patients/CancelConsultation .svg";
import NewVisitIcon from "../../../assets/images/OPD-patients/newVisit.svg";
import View from "../../../assets/images/OPD-patients/view.svg";
import LinkAbhaButton from "../Patients/LinkAbhaButton";
import { getAgoTime } from "../Patients/utils";
import {
  ensurePatientHasVisit,
  patientHasNoVisits,
  calculateAge,
  formatPatientName,
  formatDobDisplay,
} from "../../../utils/patientVisitUtils";
import CreateVisitModal from "../../../components/common/CreateVisitModal";
import EditPatientModal from "../../../components/Patients/EditPatientModal";
import PatientDetailsDrawer from "../../../components/Patients/PatientDetailsDrawer";
import {
  isValidPersonName,
  isValidMobile,
  containsExecutableMarkup,
  containsObjectArtifact,
  sanitizeSearchQuery,
} from "../../../utils/inputValidation";
import {
  getDepartments,
  getDoctors,
  completeRegistration,
} from "../../../api/apiService";
import {
  getPatientsAll,
  getPatientById,
} from "../../../Services/patientService";
import { useAuth } from "../../../context/AuthContext";
import { PERMISSIONS } from "../../../constants/permissions";
import ToastContainer, {
  useToast,
} from "../../../components/common/ToastContainer";

// Parse date-only string (YYYY-MM-DD) as local date to avoid UTC-off-by-one-day
const parseLocalDate = (dateString) => {
  if (!dateString) return null;
  const str = String(dateString).trim();
  const ymd = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (ymd) {
    const [, y, m, d] = ymd;
    return new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
  }
  const d = new Date(dateString);
  return isNaN(d.getTime()) ? null : d;
};

// Helper function to format date (parses YYYY-MM-DD as local to avoid off-by-one day)
const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = parseLocalDate(dateString) || new Date(dateString);
  if (isNaN(date.getTime())) return "";
  const options = { day: "2-digit", month: "short", year: "numeric" };
  return date.toLocaleDateString("en-GB", options);
};

// Helper function to get time ago
const getTimeAgo = (dateString) => getAgoTime(dateString);

// Helper function to format visit time
const formatVisitTime = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};

// Helper to format date for HTML date input (YYYY-MM-DD)
const formatDateForInput = (patient) => {
  if (!patient) return "";

  if (patient.yearOfBirth && patient.monthOfBirth && patient.dayOfBirth) {
    const year = patient.yearOfBirth;
    const month = String(patient.monthOfBirth).padStart(2, "0");
    const day = String(patient.dayOfBirth).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  if (patient.yearOfBirth && !patient.monthOfBirth) {
    return `${patient.yearOfBirth}-01-01`;
  }

  let dob = patient.dob || patient.dateOfBirth;
  if (!dob) return "";

  if (/^\d{4}-\d{2}-\d{2}$/.test(dob)) {
    return dob;
  }

  if (/^\d{2}-\d{2}-\d{4}$/.test(dob)) {
    const [d, m, y] = dob.split("-");
    return `${y}-${m}-${d}`;
  }

  try {
    const d = parseLocalDate(dob) || new Date(dob);
    if (d && !isNaN(d.getTime())) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    }
  } catch (e) {
    console.error("Error parsing DOB:", e);
  }

  return "";
};

const OPD = () => {
  const navigate = useNavigate();
  const { can, user } = useAuth();
  const { toasts, removeToast, showSuccess, showError } = useToast();
  const canRegisterPatient = can(PERMISSIONS.PATIENTS_REGISTER) || can(PERMISSIONS.OPD_ADD_VISIT);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalPatients, setTotalPatients] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedDrawerPatientId, setSelectedDrawerPatientId] = useState(null);
  const [abhaPatients, setAbhaPatients] = useState([]);
  const [abhaLoading, setAbhaLoading] = useState(true);
  const [abhaError, setAbhaError] = useState(null);
  const [activeTab, setActiveTab] = useState("All");
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [navigatingPatientId, setNavigatingPatientId] = useState(null);
  const [registrationForm, setRegistrationForm] = useState({
    tokenNumber: "",
    department: "",
    departmentId: "",
    doctorName: "",
    doctorId: "",
    consultationFee: 500,
    complaint: "",
    visitType: "General OPD",
    description: "",
    isEmergency: false,
    insurance: {
      provider: "",
      policyNumber: "",
    },
    payment: {
      mode: "cash",
      amount: 500,
    },
    address: {
      line: "",
      district: "",
      state: "",
      pincode: "",
    },
    name: "",
    mobile: "",
    aadhaarNumber: "",
    dob: "",
    gender: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [showVisitModal, setShowVisitModal] = useState(false);
  const [patientForVisit, setPatientForVisit] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [patientToEdit, setPatientToEdit] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0); // To trigger list refresh

  // Debounce search input changes (350ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch departments and doctors together when registration modal opens
  useEffect(() => {
    if (!showRegistrationModal) return;
    const fetchFormOptions = async () => {
      try {
        const [deptRes, docRes] = await Promise.all([
          getDepartments(),
          getDoctors(),
        ]);
        const deptList =
          deptRes?.data?.departments ||
          deptRes?.departments ||
          deptRes?.data ||
          [];
        setDepartments(Array.isArray(deptList) ? deptList : []);
        const docList =
          docRes?.data?.doctors || docRes?.doctors || docRes?.data || [];
        setDoctors(Array.isArray(docList) ? docList : []);
      } catch (error) {
        console.error("Error fetching departments/doctors:", error);
      }
    };
    fetchFormOptions();
  }, [showRegistrationModal]);

  // Fetch minimal patients data from upgraded /api/patients/all
  const fetchPatients = useCallback(async () => {
    if (activeTab === "abhaOpd") return;

    try {
      setLoading(true);
      setError(null);

      const tabParam = activeTab === "All" ? "all" : activeTab.toLowerCase();

      const response = await getPatientsAll({
        tab: tabParam,
        search: debouncedSearch,
        page: 1,
        limit: 50,
      });

      const patientList =
        response?.data?.patients ||
        (Array.isArray(response?.data) ? response.data : []) ||
        response?.patients ||
        [];

      const total =
        response?.data?.total ??
        response?.total ??
        patientList.length;

      const transformedPatients = patientList.map((patient) => {
        const rawName =
          (patient.name && typeof patient.name === "string" && patient.name.trim() && patient.name.trim() !== "undefined undefined")
            ? patient.name.trim()
            : (patient.fullName && typeof patient.fullName === "string" && patient.fullName.trim() && patient.fullName.trim() !== "undefined undefined")
            ? patient.fullName.trim()
            : formatPatientName(patient) || "Patient";

        const fullName = rawName;
        const dobFormatted = formatDobDisplay(patient.dob || patient.dateOfBirth);
        const computedAge = (() => {
          const calc = calculateAge(dobFormatted);
          if (calc && Number(calc) > 0) return calc;
          if (patient.age && !isNaN(Number(patient.age)) && Number(patient.age) > 0) return Number(patient.age);
          return "";
        })();

        const visitTimestamp =
          patient.lastVisitDate ||
          patient.time ||
          patient.appointmentDate ||
          patient.updatedAt ||
          patient.lastVisit ||
          patient.createdAt;

        const safeGet = (value) => value || "";

        const isAbhaLinked = Boolean(
          patient.isAbhaLinked === true ||
          patient.isabhalinked === true ||
          patient.isABHALinked === true ||
          String(patient.isAbhaLinked).toLowerCase() === "true" ||
          String(patient.isabhalinked).toLowerCase() === "true" ||
          String(patient.isABHALinked).toLowerCase() === "true" ||
          (patient.maskedAbha && String(patient.maskedAbha).trim() && patient.maskedAbha !== "N/A") ||
          (patient.ABHANumber && String(patient.ABHANumber).trim()) ||
          (patient.abhaaddress && String(patient.abhaaddress).trim()) ||
          (patient.abhaAddress && String(patient.abhaAddress).trim()) ||
          (patient.ABHAAddress && String(patient.ABHAAddress).trim())
        );

        const maskedAbha =
          patient.maskedAbha ||
          (patient.ABHANumber ? `**** ${String(patient.ABHANumber).slice(-4)}` : "") ||
          patient.formattedAbhaId ||
          (patient.abhaaddress || patient.abhaAddress || patient.ABHAAddress || "");

        return {
          id: patient._id || patient.id,
          _id: patient._id || patient.id,
          name: fullName,
          fullName: fullName,
          age: computedAge ? `${computedAge}yr` : "",
          calculatedAge: computedAge,
          dob: dobFormatted,
          lastVisit: formatDate(visitTimestamp),
          lastVisitAgo: getTimeAgo(visitTimestamp),
          opdId:
            safeGet(patient.opdId) || (patient._id || patient.id || "").slice(-8).toUpperCase(),
          queue:
            patient.queue !== undefined && patient.queue !== null
              ? String(patient.queue).padStart(2, "0")
              : "—",
          isAbhaLinked,
          isabhalinked: isAbhaLinked,
          maskedAbha,
          abhaId: maskedAbha || (isAbhaLinked ? "Linked" : "N/A"),
          formattedAbhaId: maskedAbha || patient.ABHANumber || "",
          uhid:
            safeGet(patient.uhid || patient.UHID) ||
            (patient._id || patient.id || "").slice(-8).toUpperCase(),
          fee:
            safeGet(
              patient.fee !== undefined && patient.fee !== ""
                ? (typeof patient.fee === "number" ? `₹${patient.fee}` : patient.fee)
                : patient.consultationFee
                ? `₹${patient.consultationFee}`
                : "",
            ),
          time: safeGet(patient.time || patient.appointmentTime || ""),
          visitType: patient.visitType || "General OPD",
          date: formatDate(patient.lastVisitDate || patient.appointmentDate || patient.time || patient.createdAt || new Date()),
          doctor:
            safeGet(
              patient.doctor ||
              patient.consultingDoctor ||
              patient.assignedDoctor ||
              patient.lastVisitedDoctor ||
              patient?.visits?.[0]?.doctor,
            ) || "",
          mobile: safeGet(patient.mobile),
          address: safeGet(patient.address),
          pincode: safeGet(patient.pincode),
          status: safeGet(patient.status),
          // Store raw patient data for OPD Details page
          rawData: {
            ...patient,
            name: fullName,
            fullName,
            calculatedAge: computedAge,
            age: computedAge ? `${computedAge}yr` : "",
            formattedAbhaId: maskedAbha || patient.ABHANumber || "",
            isAbhaLinked,
            isabhalinked: isAbhaLinked,
            maskedAbha,
            // Demographic and contact
            ABHANumber: safeGet(patient.ABHANumber),
            ABHAAddress: safeGet(
              patient.ABHAAddress || patient.abhaAddress || patient.abhaaddress,
            ),
            yearOfBirth:
              patient.yearOfBirth ||
              (patient.dob ? new Date(patient.dob).getFullYear() : null),
            bloodGroup: safeGet(patient.bloodGroup),
            allergies: patient.allergies || [],
            emergencyContact: safeGet(patient.emergencyContact),
            gender: safeGet(patient.gender),
            dateOfBirth: safeGet(patient.dob),
            email: safeGet(patient.email),
            // Medical information
            chronicConditions: patient.chronicConditions || [],
            currentMedications: patient.currentMedications || [],
            medicalHistory: patient.medicalHistory || [],
            // Visit information
            visitId: safeGet(patient.visitId),
            department: safeGet(patient.department),
            reasonForVisit: safeGet(patient.reasonForVisit),
            // Vital signs
            height: safeGet(patient.height),
            weight: safeGet(patient.weight),
            bmi: safeGet(patient.bmi),
            bmiCategory: safeGet(patient.bmiCategory),
            bloodPressure: safeGet(patient.bloodPressure),
            bloodPressureStatus: safeGet(patient.bloodPressureStatus),
            pulseRate: safeGet(patient.pulseRate),
            pulseRateStatus: safeGet(patient.pulseRateStatus),
            temperature: safeGet(patient.temperature),
            temperatureStatus: safeGet(patient.temperatureStatus),
            spO2: safeGet(patient.spO2),
            spO2Status: safeGet(patient.spO2Status),
            vitalsTimestamp: safeGet(patient.vitalsTimestamp),
          },
        };
      });

      setPatients(transformedPatients);
      setTotalPatients(total);
    } catch (err) {
      console.error("Error fetching patients:", err);
      setError(err?.response?.data?.message || err.message || "Failed to fetch patients data");
    } finally {
      setLoading(false);
    }
  }, [activeTab, debouncedSearch]);

  useEffect(() => {
    if (activeTab !== "abhaOpd") {
      fetchPatients();
    }
  }, [fetchPatients, refreshTrigger, activeTab]);

  // Function to fetch ABHA OPD pending tokens
  const fetchAbhaPatients = async () => {
    try {
      setAbhaLoading(true);

      const response = await fetch(
        `${process.env.REACT_APP_M2}/opd/pending-tokens`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      const data = await response.json();

      if (data.status === "success") {
        setAbhaPatients(data.data);
      } else {
        setAbhaError("Failed to fetch ABHA OPD data");
      }
    } catch (err) {
      console.error("Error fetching ABHA patients:", err);
      setAbhaError(err.message || "Failed to fetch ABHA OPD data");
    } finally {
      setAbhaLoading(false);
    }
  };

  // Fetch ABHA OPD pending tokens when tab is activated
  useEffect(() => {
    if (activeTab === "abhaOpd") {
      fetchAbhaPatients();
    }
  }, [activeTab]);

  // Function to handle patient click and navigate to OPD Details (creates visit if none)
  const handlePatientClick = async (patient) => {
    const patientId = patient.id ?? patient._id;
    if (!patientId || navigatingPatientId) return;

    try {
      setNavigatingPatientId(patientId);

      // Fetch full patient data (including historical visits) if not already present
      let fullPatient = patient.rawData ?? patient;
      if (!Array.isArray(fullPatient.visits)) {
        try {
          const fetched = await getPatientById(patientId);
          if (fetched) {
            fullPatient = {
              ...fullPatient,
              ...fetched,
              rawData: {
                ...(fullPatient.rawData || fullPatient),
                ...fetched,
              },
            };
          }
        } catch (fetchErr) {
          console.warn("Could not fetch full patient record before visit check:", fetchErr);
        }
      }

      if (patientHasNoVisits(fullPatient)) {
        setPatientForVisit(fullPatient);
        setShowVisitModal(true);
        return;
      }

      const patientToUse = await ensurePatientHasVisit(fullPatient);
      const dataToStore = patientToUse.rawData || fullPatient.rawData || fullPatient;
      const effectivePatientId = patientToUse.id || patientId;
      navigate(`/OPDDetails?patientId=${encodeURIComponent(effectivePatientId)}`, {
        state: {
          patientData: dataToStore,
          patientId: effectivePatientId,
        },
      });
    } catch (err) {
      console.error("Failed to navigate to patient details", err);
      showError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load patient details.",
      );
    } finally {
      setNavigatingPatientId(null);
    }
  };

  const handleVisitCreated = (result) => {
    const apiPatient = result.patientData ?? result;
    const existingRaw = patientForVisit?.rawData ?? patientForVisit;

    const mergedRawData = {
      ...existingRaw,
      ...apiPatient,
      visits: apiPatient.visits ?? existingRaw?.visits,
      visitId:
        apiPatient.visitId ??
        apiPatient.visits?.[0]?.visitId ??
        existingRaw?.visitId,
    };

    const effectivePatientId = mergedRawData._id || mergedRawData.id;
    navigate(`/OPDDetails?patientId=${encodeURIComponent(effectivePatientId)}`, {
      state: {
        patientData: mergedRawData,
        patientId: effectivePatientId,
      },
    });
  };

  const handleEditPatient = (patient) => {
    setPatientToEdit(patient.rawData || patient);
    setShowEditModal(true);
  };

  const handleUpdateSuccess = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  // Function to open registration modal
  const handleRegisterClick = (patient) => {
    setSelectedPatient(patient);

    // Auto-fill form with patient data from API
    setRegistrationForm({
      tokenNumber: patient.tokenNumber || "",
      department: "",
      departmentId: "",
      doctorName: "",
      doctorId: "",
      consultationFee: 500,
      complaint: "",
      visitType: "General Visit",
      description: "",
      isEmergency: false,
      insurance: {
        provider: "",
        policyNumber: "",
      },
      payment: {
        mode: "cash",
        amount: 500,
      },
      address: {
        line: patient.address?.line || "",
        district: patient.address?.district || "",
        state: patient.address?.state || "",
        pincode: patient.address?.pincode || "",
      },
      name: patient.name || "",
      mobile: patient.mobile || "",
      aadhaarNumber: "",
      dob: formatDateForInput(patient),
      gender: patient.gender || "",
    });

    setShowRegistrationModal(true);
  };

  // Handle form input changes
  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "departmentId") {
      const selectedDept = departments.find((d) => d._id === value);
      setRegistrationForm((prev) => ({
        ...prev,
        departmentId: value,
        department: selectedDept ? selectedDept.name : "",
      }));
    } else if (name === "doctorId") {
      const selectedDoc = doctors.find((d) => d._id === value);
      const docName = selectedDoc
        ? selectedDoc.name ||
          `${selectedDoc.firstName || selectedDoc.f_name || ""} ${selectedDoc.lastName || selectedDoc.l_name || ""}`.trim()
        : "";
      // Auto-set department from doctor if not already set
      const autoDeptId =
        selectedDoc?.department?._id || selectedDoc?.department || "";
      const autoDept = departments.find((d) => d._id === autoDeptId);
      setRegistrationForm((prev) => ({
        ...prev,
        doctorId: value,
        doctorName: docName,
        departmentId: prev.departmentId || autoDeptId,
        department: prev.department || (autoDept ? autoDept.name : ""),
        consultationFee: selectedDoc?.consultationFee ?? prev.consultationFee,
        payment: {
          ...prev.payment,
          amount: selectedDoc?.consultationFee ?? prev.payment.amount,
        },
      }));
    } else if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setRegistrationForm((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === "checkbox" ? checked : value,
        },
      }));
    } else {
      setRegistrationForm((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  // Submit registration
  const handleSubmitRegistration = async (e) => {
    e.preventDefault();

    if (!canRegisterPatient) {
      showError("You do not have permission to register patients or create visits.");
      return;
    }

    if (!selectedPatient || !selectedPatient._id) {
      showError("Patient ID is missing");
      return;
    }

    const cleanName = (registrationForm.name || "").trim();
    if (!cleanName || cleanName.length < 2) {
      showError("Please enter a valid patient name (at least 2 characters).");
      return;
    }
    if (
      containsExecutableMarkup(registrationForm.name) ||
      containsObjectArtifact(registrationForm.name)
    ) {
      showError("Patient name contains invalid characters or HTML/script tags.");
      return;
    }

    const nameParts = cleanName.split(/\s+/).filter(Boolean);
    const f_name = nameParts[0] || "";
    const l_name = nameParts.length > 1 ? nameParts[nameParts.length - 1] : "";
    const m_name = nameParts.length > 2 ? nameParts.slice(1, -1).join(" ") : "";

    if (!isValidPersonName(f_name)) {
      showError("First name must be at least 2 characters and contain only letters.");
      return;
    }
    if (l_name && !isValidPersonName(l_name)) {
      showError("Last name must be at least 2 characters and contain only letters.");
      return;
    }
    if (registrationForm.mobile && !isValidMobile(registrationForm.mobile)) {
      showError("Mobile number must be a valid 10-digit number starting with 6, 7, 8, or 9.");
      return;
    }

    try {
      setSubmitting(true);

      // Ensure patient profile modifications (Name, Gender, DOB) are sent
      const payload = {
        ...registrationForm,
        f_name,
        m_name,
        l_name,
        gender: registrationForm.gender,
        dob: registrationForm.dob,
      };

      const result = await completeRegistration(selectedPatient._id, payload);

      // Log the response to debug the structure
      console.log("Registration result:", result);

      // Close modal and show success - the API returned a successful response
      showSuccess("Registration completed successfully!");
      setShowRegistrationModal(false);

      // Give a brief moment for the modal to close, then reload
      setTimeout(() => {
        window.location.reload();
      }, 700);
    } catch (error) {
      console.error("Registration error:", error);
      showError(
        error.response?.data?.message ||
          error.message ||
          "An error occurred during registration.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <style>
        {`
                    .patient-row-hover:hover {
                        background-color: #f8f9fa !important;
                        transition: background-color 0.2s ease;
                    }
                    .patient-row-hover:hover td {
                        background-color: inherit !important;
                    }
                `}
      </style>
      <div className="container-fluid">
        <div className="opd-page">
          <div className="header-section-new">
            <div className="header-left">
              <ul className="nav nav-tabs-new">
                <li className="nav-item">
                  <span
                    className={`nav-link ${activeTab === "All" ? "active" : ""}`}
                    onClick={() => setActiveTab("All")}
                  >
                    All
                  </span>
                </li>
                <li className="nav-item">
                  <span
                    className={`nav-link ${activeTab === "OPD" ? "active" : ""}`}
                    onClick={() => setActiveTab("OPD")}
                  >
                    OPD
                  </span>
                </li>
                <li className="nav-item">
                  <span
                    className={`nav-link ${activeTab === "IPD" ? "active" : ""}`}
                    onClick={() => setActiveTab("IPD")}
                  >
                    IPD
                  </span>
                </li>
                <li className="nav-item">
                  <span
                    className={`nav-link ${activeTab === "Emergency" ? "active" : ""}`}
                    onClick={() => setActiveTab("Emergency")}
                  >
                    Emergency Cases
                  </span>
                </li>
                <li className="nav-item">
                  <span
                    className={`nav-link ${activeTab === "abhaOpd" ? "active" : ""}`}
                    onClick={() => setActiveTab("abhaOpd")}
                  >
                    ABHA OPD Registrations
                  </span>
                </li>
              </ul>
            </div>
            <div className="header-right">
              <div className="search-bar-new">
                <span className="search-icon">
                  <img src={Search} alt="Search" />
                </span>
                <input
                  type="text"
                  placeholder="Search by Name, Mobile, ID, or Doctor"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(sanitizeSearchQuery(e.target.value))}
                />
              </div>
              <button
                className="btn-refresh-new"
                onClick={() => setRefreshTrigger((prev) => prev + 1)}
                disabled={loading}
              >
                {loading ? "Refreshing..." : "↻ Refresh"}
              </button>
              {can(PERMISSIONS.PATIENTS_REGISTER) && (
                <Link to="/add-patient" className="btn-add-new">
                  + Add Patient
                </Link>
              )}
            </div>
          </div>

          <div className="OpdBodySection">
            <div className="tab-content">
              <div
                id="All"
                className={`tab-pane ${activeTab !== "abhaOpd" ? "active show" : ""}`}
                style={{ display: activeTab === "abhaOpd" ? "none" : "" }}
              >
                <br />
                <div className="opdAllTable">
                  <table className="table table-bordered align-middle">
                    <thead>
                      <tr>
                        <th>Patient Name</th>
                        <th>OPD ID</th>
                        <th>Queue</th>
                        <th>ABHA ID</th>
                        <th>UHID</th>
                        <th>Fee</th>
                        <th>Time</th>
                        <th>Doctor</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="9" className="text-center py-4">
                            <div
                              className="spinner-border text-primary"
                              role="status"
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                            <p className="mt-2 mb-0">Loading patients...</p>
                          </td>
                        </tr>
                      ) : error ? (
                        <tr>
                          <td colSpan="9" className="text-center py-4">
                            <div className="alert alert-danger mb-0">
                              <strong>Error:</strong> {error}
                              <br />
                              <button
                                className="btn btn-sm btn-outline-primary mt-2"
                                onClick={() => setRefreshTrigger((prev) => prev + 1)}
                              >
                                Retry
                              </button>
                            </div>
                          </td>
                        </tr>
                      ) : patients.length === 0 ? (
                        <tr>
                          <td colSpan="9" className="text-center py-4">
                            <div className="alert alert-info mb-0">
                              <strong>No patients found.</strong>
                              <br />
                              {can(PERMISSIONS.PATIENTS_REGISTER) && (
                                <Link
                                  to="/add-patient"
                                  className="btn btn-sm btn-primary mt-2"
                                >
                                  Add First Patient
                                </Link>
                              )}
                            </div>
                          </td>
                        </tr>
                      ) : (
                        patients
                          .filter((patient) => {
                            if (
                              activeTab === "OPD" &&
                              patient.visitType &&
                              patient.visitType !== "General OPD" &&
                              patient.visitType !== "OPD"
                            ) {
                              return false;
                            }
                            if (
                              activeTab === "IPD" &&
                              patient.visitType &&
                              patient.visitType !== "IPD"
                            ) {
                              return false;
                            }
                            if (
                              activeTab === "Emergency" &&
                              patient.visitType &&
                              patient.visitType !== "Emergency"
                            ) {
                              return false;
                            }
                            return true;
                          })
                          .map((patient, index) => (
                            <tr
                              key={patient.id || index}
                              style={{
                                cursor: navigatingPatientId
                                  ? "wait"
                                  : "pointer",
                                opacity:
                                  navigatingPatientId ===
                                  (patient.id ?? patient._id)
                                    ? 0.7
                                    : 1,
                              }}
                              onClick={() =>
                                handlePatientClick(patient)
                              }
                              title="Click to view patient consultation"
                              className="patient-row-hover"
                            >
                              <td>
                                <div className="patientDetails">
                                  <h5>{patient.name || patient.fullName || "Patient"}</h5>
                                  <p>
                                    Age: {patient.age || "—"}
                                    {patient.dob && typeof patient.dob === "string"
                                      ? ` | DOB: ${patient.dob.split("T")[0]}`
                                      : ""}
                                  </p>
                                  {patient.lastVisit ? (
                                    <p>
                                      Last Visit: {patient.lastVisit}
                                      {patient.lastVisitAgo ? ` (${patient.lastVisitAgo})` : ""}
                                    </p>
                                  ) : null}
                                  {patient.mobile && (
                                    <p className="text-muted small">
                                      Mobile: {patient.mobile}
                                    </p>
                                  )}
                                  {navigatingPatientId ===
                                    (patient.id ?? patient._id) && (
                                    <p className="text-primary small mb-0 mt-1">
                                      <span
                                        className="spinner-border spinner-border-sm me-1"
                                        role="status"
                                        aria-hidden="true"
                                      />
                                      Loading consultation…
                                    </p>
                                  )}
                                </div>
                              </td>
                              <td>
                                <h5>{patient.opdId}</h5>
                              </td>
                              <td>
                                <h5>
                                  {patient.queue.toString().padStart(2, "0")}
                                </h5>
                              </td>
                              <td onClick={(e) => e.stopPropagation()}>
                                {patient.isAbhaLinked || (patient.abhaId && patient.abhaId !== "N/A") ? (
                                  <h5 style={{ fontFamily: "monospace", margin: 0 }}>
                                    {patient.maskedAbha || patient.abhaId || "Linked"}
                                  </h5>
                                ) : (
                                  <LinkAbhaButton patient={patient} />
                                )}
                              </td>
                              <td>
                                <h5>{patient.uhid}</h5>
                              </td>
                              <td>
                                <h5>{patient.fee || ""}</h5>
                              </td>
                              <td>
                                <div className="patientTime">
                                  <h5>{patient.time || ""}</h5>
                                  <p>{patient.date}</p>
                                </div>
                              </td>
                              <td>
                                <h5>{patient.doctor || ""}</h5>
                              </td>
                              <td onClick={(e) => e.stopPropagation()}>
                                <div
                                  className="dropdown"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <div
                                    className="menudropdown"
                                    id={`dropdownMenuButton${index}`}
                                    data-bs-toggle="dropdown"
                                    aria-expanded="false"
                                  >
                                    <img src={MenuHorizental} alt="menu" />
                                  </div>
                                  <ul
                                    className="dropdown-menu dropdown-menu-end"
                                    aria-labelledby={`dropdownMenuButton${index}`}
                                  >
                                    <li>
                                      <button
                                        className="dropdown-item"
                                        type="button"
                                        onClick={() =>
                                          setSelectedDrawerPatientId(
                                            patient.id || patient._id,
                                          )
                                        }
                                      >
                                        <img
                                          src={View}
                                          alt="View"
                                          style={{
                                            width: "16px",
                                            height: "16px",
                                            marginRight: "8px",
                                          }}
                                        />{" "}
                                        View Details
                                      </button>
                                    </li>
                                    <li>
                                      <button
                                        className="dropdown-item"
                                        type="button"
                                        onClick={() =>
                                          handlePatientClick(patient)
                                        }
                                      >
                                        <img
                                          src={ManualPrescription}
                                          alt="Consultation"
                                          style={{
                                            width: "16px",
                                            height: "16px",
                                            marginRight: "8px",
                                          }}
                                        />{" "}
                                        Open Consultation
                                      </button>
                                    </li>
                                    <li>
                                      <button
                                        className="dropdown-item"
                                        type="button"
                                        onClick={() => {
                                          setPatientForVisit(patient);
                                          setShowVisitModal(true);
                                        }}
                                      >
                                        <img
                                          src={NewVisitIcon}
                                          alt="New Visit"
                                          style={{
                                            width: "16px",
                                            height: "16px",
                                            marginRight: "8px",
                                          }}
                                        />{" "}
                                        New Visit
                                      </button>
                                    </li>
                                    <li>
                                      <button
                                        className="dropdown-item"
                                        type="button"
                                        onClick={() =>
                                          handleEditPatient(patient)
                                        }
                                      >
                                        <img src={EditPatient} alt="Edit" />{" "}
                                        Edit Patient
                                      </button>
                                    </li>
                                    <li>
                                      <Link
                                        className="dropdown-item"
                                        to={`/opd-assessment?patientId=${patient.id}`}
                                        state={{
                                          patientData: patient.rawData,
                                          patientId: patient.id,
                                        }}
                                      >
                                        <img
                                          src={Assessment}
                                          alt="Assessment"
                                        />{" "}
                                        Assessment{" "}
                                      </Link>
                                    </li>
                                    {/* <li>
                                      <button
                                        className="dropdown-item"
                                        type="button"
                                      >
                                        <img src={OPDIDPrint} alt="Print" /> OPD
                                        ID Print
                                      </button>
                                    </li> */}
                                    <li>
                                      <button
                                        className="dropdown-item"
                                        type="button"
                                        onClick={() =>
                                          handlePatientClick(patient)
                                        }
                                      >
                                        <img
                                          src={ManualPrescription}
                                          alt="Prescription"
                                        />{" "}
                                        Manual Prescription{" "}
                                      </button>
                                    </li>
                                    {/* <li>
                                      <button
                                        className="dropdown-item"
                                        type="button"
                                      >
                                        <img
                                          src={PaymentReceipt}
                                          alt="Receipt"
                                        />{" "}
                                        Payment Receipt{" "}
                                      </button>
                                    </li> */}
                                    {/* <li>
                                      <button
                                        className="dropdown-item"
                                        type="button"
                                      >
                                        <img src={XRayReport} alt="X-Ray" />{" "}
                                        X-Ray Report
                                      </button>
                                    </li> */}
                                    {/* <li>
                                      <button
                                        className="dropdown-item"
                                        type="button"
                                      >
                                        <img src={ScanningReport} alt="Scan" />{" "}
                                        Scanning Report
                                      </button>
                                    </li> */}
                                    <li>
                                      <Link
                                        className="dropdown-item"
                                        to={`/OPDDetails?patientId=${patient.id}`}
                                        state={{
                                          patientData: patient.rawData,
                                          patientId: patient.id,
                                        }}
                                      >
                                        <img src={LabReport} alt="Lab" /> Lab
                                        Report
                                      </Link>
                                    </li>
                                    {/* <li>
                                      <button
                                        className="dropdown-item"
                                        type="button"
                                      >
                                        <img
                                          src={CancelConsultation}
                                          alt="Cancel"
                                        />{" "}
                                        Cancel Consultation{" "}
                                      </button>
                                    </li> */}
                                  </ul>
                                </div>
                              </td>
                            </tr>
                          ))
                      )}
                    </tbody>
                  </table>
                  {/* Show total patients count */}
                  {!loading && !error && patients.length > 0 && (
                    <div className="mt-3 text-muted">
                      <small>Total Patients: {totalPatients}</small>
                    </div>
                  )}
                </div>
              </div>
              <div
                id="abhaOpd"
                className={`tab-pane ${activeTab === "abhaOpd" ? "active show" : "fade"}`}
              >
                <br />
                {abhaLoading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2 mb-0">Loading ABHA OPD patients...</p>
                  </div>
                ) : abhaError ? (
                  <div className="alert alert-danger">
                    <strong>Error:</strong> {abhaError}
                    <br />
                    <button
                      className="btn btn-sm btn-outline-primary mt-2"
                      onClick={() => window.location.reload()}
                    >
                      Retry
                    </button>
                  </div>
                ) : abhaPatients.length === 0 ? (
                  <div className="alert alert-info">
                    <strong>No pending ABHA OPD tokens found.</strong>
                  </div>
                ) : (
                  <div className="row">
                    {abhaPatients.map((patient, index) => (
                      <div key={patient._id} className="col-xs-12 col-md-4">
                        <div className="abhaOpdCard">
                          <div className="abhaCardHeader">
                            <div className="count">{patient.tokenNumber}</div>
                            <div className="abhaNameDetails">
                              <div className="name">{patient.name}</div>
                              <div className="details">
                                {patient.gender}: {patient.age}yr -{" "}
                                {patient.mobile}
                                {patient.dob
                                  ? ` | DOB: ${patient.dob.split("T")[0]}`
                                  : ""}
                              </div>
                            </div>
                            <div className="tocken">
                              Token # {patient.tokenNumber}
                            </div>
                          </div>
                          <div className="tokenTimeDetails">
                            <span>
                              Token Date : {formatDate(patient.visitDate)}
                            </span>
                            <span>
                              Time : {formatVisitTime(patient.visitDate)}
                            </span>
                          </div>
                          {patient.abhaAddress && (
                            <div className="px-3 py-2">
                              <small className="text-muted">
                                ABHA: {patient.abhaAddress}
                              </small>
                            </div>
                          )}
                          {patient.abhaNumber && (
                            <div className="px-3 py-1">
                              <small className="text-muted">
                                ABHA Number: {patient.abhaNumber}
                              </small>
                            </div>
                          )}
                          <div className="abhaOpdAction">
                            <button
                              className="registerPatients"
                              onClick={() => handleRegisterClick(patient)}
                            >
                              Register Patient
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div id="Consultation" className=" tab-pane fade">
                <br />
                <h3>Consultation</h3>
                <p>
                  Sed ut perspiciatis unde omnis iste natus error sit voluptatem
                  accusantium doloremque laudantium, totam rem aperiam.
                </p>
              </div>
              <div id="MedicalRecords" className=" tab-pane fade">
                <br />
                <h3>MedicalRecords</h3>
                <p>
                  Sed ut perspiciatis unde omnis iste natus error sit voluptatem
                  accusantium doloremque laudantium, totam rem aperiam.
                </p>
              </div>
              <div id="DischargeSummary" className=" tab-pane fade">
                <br />
                <h3>DischargeSummary</h3>
                <p>
                  Sed ut perspiciatis unde omnis iste natus error sit voluptatem
                  accusantium doloremque laudantium, totam rem aperiam.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Registration Modal */}
      {showRegistrationModal && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 9999 }}
        >
          <div
            className="modal-dialog modal-lg"
            style={{ maxWidth: "800px", margin: "1.75rem auto" }}
          >
            <div
              className="modal-content"
              style={{
                maxHeight: "90vh",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div className="modal-header">
                <h5 className="modal-title">Patient Registration</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowRegistrationModal(false)}
                ></button>
              </div>
              <form
                onSubmit={handleSubmitRegistration}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  flex: 1,
                  minHeight: 0,
                }}
              >
                <div
                  className="modal-body"
                  style={{ overflowY: "auto", flex: 1 }}
                >
                  {!canRegisterPatient && (
                    <div
                      className="alert alert-info py-2 px-3 mb-3 d-flex align-items-center gap-2"
                      role="alert"
                      style={{
                        borderRadius: "6px",
                        fontSize: "13px",
                        backgroundColor: "#EBF5FF",
                        color: "#1E429F",
                        border: "1px solid #B4C6FC",
                      }}
                    >
                      <i className="material-icons" style={{ fontSize: "18px" }}>visibility</i>
                      <span><strong>Read-Only Mode:</strong> You do not have permission to register patients or create visits. All fields are locked for viewing only.</span>
                    </div>
                  )}
                  <fieldset
                    disabled={!canRegisterPatient}
                    style={{
                      border: "none",
                      padding: 0,
                      margin: 0,
                      pointerEvents: !canRegisterPatient ? "none" : "auto",
                      opacity: !canRegisterPatient ? 0.9 : 1,
                    }}
                  >
                  <div className="row g-3">
                    {/* Token Number */}
                    <div className="col-md-6">
                      <label className="form-label">Token Number</label>
                      <input
                        type="text"
                        className="form-control"
                        name="tokenNumber"
                        value={registrationForm.tokenNumber}
                        onChange={handleFormChange}
                        readOnly
                      />
                    </div>

                    {/* Name */}
                    <div className="col-md-6">
                      <label className="form-label">Patient Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        name="name"
                        value={registrationForm.name}
                        onChange={handleFormChange}
                        required
                      />
                    </div>

                    {/* Mobile */}
                    <div className="col-md-6">
                      <label className="form-label">Mobile *</label>
                      <input
                        type="tel"
                        className="form-control"
                        name="mobile"
                        value={registrationForm.mobile}
                        onChange={handleFormChange}
                        required
                      />
                    </div>

                    {/* Gender */}
                    <div className="col-md-6">
                      <label className="form-label">Gender *</label>
                      <select
                        className="form-select"
                        name="gender"
                        value={registrationForm.gender}
                        onChange={handleFormChange}
                        required
                      >
                        <option value="">Select Gender</option>
                        <option value="M">Male</option>
                        <option value="F">Female</option>
                        <option value="O">Other</option>
                      </select>
                    </div>

                    {/* Date of Birth */}
                    <div className="col-md-6">
                      <label className="form-label">Date of Birth *</label>
                      <input
                        type="date"
                        className="form-control"
                        name="dob"
                        value={registrationForm.dob}
                        onChange={handleFormChange}
                        required
                      />
                    </div>

                    {/* Aadhaar Number */}
                    <div className="col-md-6">
                      <label className="form-label">Aadhaar Number</label>
                      <input
                        type="text"
                        className="form-control"
                        name="aadhaarNumber"
                        value={registrationForm.aadhaarNumber}
                        onChange={handleFormChange}
                        maxLength="12"
                      />
                    </div>

                    {/* Department */}
                    <div className="col-md-6">
                      <label className="form-label">Department *</label>
                      <select
                        className="form-select"
                        name="departmentId"
                        value={registrationForm.departmentId}
                        onChange={handleFormChange}
                        required
                      >
                        <option value="">Select Department</option>
                        {departments.map((dept) => (
                          <option key={dept._id} value={dept._id}>
                            {dept.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Doctor Name */}
                    <div className="col-md-6">
                      <label className="form-label">Doctor Name *</label>
                      <select
                        className="form-select"
                        name="doctorId"
                        value={registrationForm.doctorId}
                        onChange={handleFormChange}
                        required
                      >
                        <option value="">Select Doctor</option>
                        {doctors
                          .filter(
                            (doc) =>
                              !registrationForm.departmentId ||
                              (doc.department?._id || doc.department) ===
                                registrationForm.departmentId,
                          )
                          .map((doc) => (
                            <option key={doc._id} value={doc._id}>
                              {doc.firstName || doc.f_name || ""}{" "}
                              {doc.lastName || doc.l_name || ""}
                            </option>
                          ))}
                      </select>
                    </div>

                    {/* Visit Type */}
                    <div className="col-md-6">
                      <label className="form-label">Visit Type *</label>
                      <select
                        className="form-select"
                        name="visitType"
                        value={registrationForm.visitType}
                        onChange={handleFormChange}
                        required
                      >
                        <option value="General OPD">General OPD</option>
                        <option value="IPD">IPD</option>
                        <option value="Emergency">Emergency</option>
                        <option value="Teleconsultation">
                          Teleconsultation
                        </option>
                      </select>
                    </div>

                    {/* Description */}
                    <div className="col-md-6">
                      <label className="form-label">Visit Description</label>
                      <input
                        type="text"
                        className="form-control"
                        name="description"
                        value={registrationForm.description}
                        onChange={handleFormChange}
                        placeholder="Additional notes for this visit"
                      />
                    </div>

                    {/* Consultation Fee */}
                    <div className="col-md-6">
                      <label className="form-label">Consultation Fee *</label>
                      <input
                        type="number"
                        className="form-control"
                        name="consultationFee"
                        value={registrationForm.consultationFee}
                        onChange={handleFormChange}
                        required
                      />
                    </div>

                    {/* Complaint */}
                    <div className="col-md-6">
                      <label className="form-label">Complaint</label>
                      <input
                        type="text"
                        className="form-control"
                        name="complaint"
                        value={registrationForm.complaint}
                        onChange={handleFormChange}
                      />
                    </div>

                    {/* Is Emergency */}
                    <div className="col-md-6">
                      <div className="form-check mt-4">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          name="isEmergency"
                          checked={registrationForm.isEmergency}
                          onChange={handleFormChange}
                        />
                        <label className="form-check-label">
                          Emergency Case
                        </label>
                      </div>
                    </div>

                    {/* Address Line */}
                    <div className="col-12">
                      <label className="form-label">Address Line *</label>
                      <input
                        type="text"
                        className="form-control"
                        name="address.line"
                        value={registrationForm.address.line}
                        onChange={handleFormChange}
                        required
                      />
                    </div>

                    {/* District */}
                    <div className="col-md-4">
                      <label className="form-label">District *</label>
                      <input
                        type="text"
                        className="form-control"
                        name="address.district"
                        value={registrationForm.address.district}
                        onChange={handleFormChange}
                        required
                      />
                    </div>

                    {/* State */}
                    <div className="col-md-4">
                      <label className="form-label">State *</label>
                      <input
                        type="text"
                        className="form-control"
                        name="address.state"
                        value={registrationForm.address.state}
                        onChange={handleFormChange}
                        required
                      />
                    </div>

                    {/* Pincode */}
                    <div className="col-md-4">
                      <label className="form-label">Pincode *</label>
                      <input
                        type="text"
                        className="form-control"
                        name="address.pincode"
                        value={registrationForm.address.pincode}
                        onChange={handleFormChange}
                        required
                        maxLength="6"
                      />
                    </div>

                    {/* Insurance Provider */}
                    <div className="col-md-6">
                      <label className="form-label">Insurance Provider</label>
                      <input
                        type="text"
                        className="form-control"
                        name="insurance.provider"
                        value={registrationForm.insurance.provider}
                        onChange={handleFormChange}
                      />
                    </div>

                    {/* Insurance Policy Number */}
                    <div className="col-md-6">
                      <label className="form-label">Policy Number</label>
                      <input
                        type="text"
                        className="form-control"
                        name="insurance.policyNumber"
                        value={registrationForm.insurance.policyNumber}
                        onChange={handleFormChange}
                      />
                    </div>

                    {/* Payment Mode */}
                    <div className="col-md-6">
                      <label className="form-label">Payment Mode *</label>
                      <select
                        className="form-select"
                        name="payment.mode"
                        value={registrationForm.payment.mode}
                        onChange={handleFormChange}
                        required
                      >
                        <option value="cash">Cash</option>
                        <option value="card">Card</option>
                        <option value="upi">UPI</option>
                        <option value="online">Online</option>
                      </select>
                    </div>

                    {/* Payment Amount */}
                    <div className="col-md-6">
                      <label className="form-label">Payment Amount *</label>
                      <input
                        type="number"
                        className="form-control"
                        name="payment.amount"
                        value={registrationForm.payment.amount}
                        onChange={handleFormChange}
                        required
                      />
                    </div>
                  </div>
                  </fieldset>
                </div>
                <div className="modal-footer">
                  {!canRegisterPatient ? (
                    <div className="d-flex align-items-center justify-content-between w-100">
                      <span className="badge bg-secondary px-3 py-2" style={{ fontSize: "12px" }}>
                        Read-Only Mode ({user?.role_name || "Viewing"})
                      </span>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setShowRegistrationModal(false)}
                      >
                        Close
                      </button>
                    </div>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setShowRegistrationModal(false)}
                        disabled={submitting}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={submitting}
                      >
                        {submitting ? "Registering..." : "Register Patient"}
                      </button>
                    </>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <CreateVisitModal
        isOpen={showVisitModal}
        onClose={() => setShowVisitModal(false)}
        patientId={patientForVisit?.id}
        patientName={patientForVisit?.name}
        patientMeta={
          patientForVisit
            ? `${patientForVisit.age}${patientForVisit.dob ? ` / DOB: ${patientForVisit.dob.split("T")[0]}` : ""} / ${patientForVisit.mobile || ""} / ${patientForVisit.address || ""}`
                .replace(/\s*\/\s*\/\s*/g, " / ")
                .trim()
                .replace(/\/$/, "")
                .trim()
            : ""
        }
        onVisitCreated={handleVisitCreated}
      />
      <EditPatientModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        patientData={patientToEdit}
        onUpdateSuccess={handleUpdateSuccess}
      />

      {selectedDrawerPatientId && (
        <PatientDetailsDrawer
          patientId={selectedDrawerPatientId}
          onClose={() => setSelectedDrawerPatientId(null)}
          onOpenOpdDetails={(fullPatient) => {
            setSelectedDrawerPatientId(null);
            handlePatientClick(fullPatient);
          }}
        />
      )}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default OPD;
